import React, { useEffect, useMemo, useState } from "react";
import KycStatus from "./KycStatus";
import { Alert } from "@mui/material";
import {
  User,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Shield,
  Lock,
  X,
} from "lucide-react";
import Select from "react-select";
import { ApiHook } from "../../hooks/apiHook";
import { useQueryClient } from "@tanstack/react-query";
import ContactNotUpdate from "./ContactNotUpdate";
import { formatDate, formatDateForInput } from "../../utils/formateDate";
import Loading from "./Loading";
import { useNavigate } from "react-router";

const InputField = ({
  label,
  icon: Icon,
  error,
  type = "text",
  name,
  value,
  onChange,
  ...props
}) => (
  <div className="form-group">
    <label className="form-label">
      {Icon && <Icon size={16} />}
      {label} {props.required && <span style={{ color: "#dc3545" }}>*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      className={`form-input ${error ? "form-input-error" : ""}`}
      {...props}
    />
    {error && (
      <span className="error-text">
        <AlertCircle size={14} />
        {error}
      </span>
    )}
  </div>
);

const KycDetailsCustom = () => {
  const [activeTab, setActiveTab] = useState("personal");
  const [kycStatus, setKycStatus] = useState(null);
  const [notification, setNotification] = useState(null);
  const [reVerify, setReVerify] = useState(false)

  //Reusing
  const navigate = useNavigate()
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(null)

  const details = ApiHook.CallKycDetails();
  const kycMutation = ApiHook.CallKycUploads();
  
  const kycDocDetails = details?.data?.kycDocDetails
  const documentTypesOptions = details?.data?.kycCategory;
  const countryOptions = details?.data?.parsedCountries

  // Document field requirements configuration
  const documentFieldRequirements = useMemo(() => {
    const requirements = {
      'PASSPORT': {
        documentNumber: { required: true, label: 'Passport Number' },
        additionalNumber: { required: false, label: 'Additional Number' },
        issueDate: { required: true, label: 'Issue Date' },
        expiryDate: { required: true, label: 'Expiry Date' },
        issuingAuthority: { required: false, label: 'Issuing Authority' }
      },
      'DRIVERS_LICENSE': {
        documentNumber: { required: true, label: 'License Number' },
        additionalNumber: { required: false, label: 'Additional Number' },
        issueDate: { required: true, label: 'Issue Date' },
        expiryDate: { required: true, label: 'Expiry Date' },
        issuingAuthority: { required: false, label: 'Issuing Authority' }
      },
      'GOVERNMENT_ID': {
        documentNumber: { required: true, label: 'ID Number' },
        additionalNumber: { required: false, label: 'Tax ID Number' },
        issueDate: { required: true, label: 'Issue Date' },
        expiryDate: { required: true, label: 'Expiry Date' },
        issuingAuthority: { required: false, label: 'Issuing Authority' }
      },
      'ADDRESS_DOCUMENT': {
        documentNumber: { required: false, label: 'Document Number' },
        additionalNumber: { required: false, label: 'Additional Number' },
        issueDate: { required: false, label: 'Issue Date' },
        expiryDate: { required: false, label: 'Expiry Date' },
        issuingAuthority: { required: false, label: 'Issuing Authority' }
      },
      'DOMESTIC_PASSPORT': {
        documentNumber: { required: true, label: 'Passport Number' },
        additionalNumber: { required: false, label: 'Additional Number' },
        issueDate: { required: true, label: 'Issue Date' },
        expiryDate: { required: true, label: 'Expiry Date' },
        issuingAuthority: { required: false, label: 'Issuing Authority' }
      }
    };
    return requirements;
  }, []);

  
  // Get current document requirements
  const getCurrentDocumentRequirements = () => {
    const selectedDoc = documentTypesOptions?.find(doc => doc.value === selectedCategory);
    if (!selectedDoc) return {};
    return documentFieldRequirements[selectedDoc.value] || {};
  };

  // Fixed handleCategoryChange to work with react-select
  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption?.value || 1);
    setFormData((prev) => ({
      ...prev,
      documentCategory: selectedOption?.value || 1,
      // Clear document-specific fields when category changes
      documentNumber: "",
      additionalNumber: "",
      issueDate: "",
      expiryDate: "",
      issuingAuthority: ""
    }));
    // Clear document-related errors
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.documentCategory;
      delete newErrors.documentNumber;
      delete newErrors.additionalNumber;
      delete newErrors.issueDate;
      delete newErrors.expiryDate;
      delete newErrors.issuingAuthority;
      return newErrors;
    });
  };

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption?.value || 1);
    setFormData((prev) => ({
      ...prev,
      country: selectedOption?.value || 1,
    }));
    if (errors['country']) {
      setErrors((prev) => ({
        ...prev,
        ["country"]: "",
      }));
    }
  }

  const userData = details?.data?.userData;
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    documentCategory: "",
    address: "",
    address2: "",
    country: "",
    state: "",
    city: "",
    pin: "",
    documentNumber: "",
    additionalNumber: "",
    streetName: "",
    district: "",
    province: "",
    issueDate: "",
    expiryDate: "",
    issuingAuthority: "",
  });

  useEffect(() => {
    if (userData) {
      setFormData((prev) => ({
        ...prev,
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        dateOfBirth: userData?.dateOfBirth
          ? formatDateForInput(userData?.dateOfBirth)
          : "",
        address: userData?.address1 || "",
        address2: userData?.address2 || "",
        state: userData?.state || "",
        city: userData?.city || "",
        pin: userData?.pin || "",
      }));
    }
  }, [userData]);

  const [uploadedFiles, setUploadedFiles] = useState({
    frontDocument: null,
    backDocument: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isContactUpdated, setIsContactUpdated] = useState(
    details?.data?.isContactUpdated
  );
  useEffect(() => {
    setIsContactUpdated(details?.data?.isContactUpdated);
 }, [details?.data?.isContactUpdated]);
 
 useEffect(() => {
    setKycStatus(kycDocDetails?.kycStatus);
 }, [kycDocDetails?.kycStatus]);

  // Show notification function
  const showNotification = (message, type = "error") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Close notification function
  const closeNotification = () => {
    setNotification(null);
  };

  // Fixed handleInputChange to ensure proper state updates
  const handleInputChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validation for personal information tab
  const validatePersonalInfo = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";

    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!formData.dateOfBirth)
      newErrors.dateOfBirth = "Date of birth is required";

    if (!formData.address.trim()) newErrors.address = "Address is required";

    if (!formData.address2.trim())
      newErrors.address2 = "Address line 2 is required";

    if (!formData.city.trim()) newErrors.city = "City is required";

    if (!formData.state.trim()) newErrors.state = "State is required";

    if (!selectedCountry) newErrors.country = "Country is required";

    if (!formData.pin.trim()) newErrors.pin = "ZIP code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validation for documents tab with dynamic requirements
  const validateDocuments = () => {
    const newErrors = {};
    const requirements = getCurrentDocumentRequirements();

    if (!formData.documentCategory)
      newErrors.documentCategory = "Document type is required";

    // Validate based on document type requirements
    Object.entries(requirements).forEach(([fieldName, config]) => {
      if (config.required && !formData[fieldName]?.trim()) {
        newErrors[fieldName] = `${config.label} is required`;
      }
    });

    // Validate that expiry date is after issue date (if both are present and required)
    if (formData.issueDate && formData.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.issueDate)) {
        newErrors.expiryDate = "Expiry date must be after issue date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if personal info is complete
  const isPersonalInfoComplete = () => {
    return (
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.dateOfBirth &&
      formData.address.trim() &&
      formData.address2.trim() &&
      selectedCountry &&
      formData.state.trim() &&
      formData.city.trim() &&
      formData.pin.trim()
    );
  };

  // Check if documents section is complete based on requirements
  const isDocumentsComplete = () => {
    if (!formData.documentCategory || !uploadedFiles.frontDocument || !uploadedFiles.backDocument) {
      return false;
    }

    const requirements = getCurrentDocumentRequirements();
    
    // Check all required fields
    for (const [fieldName, config] of Object.entries(requirements)) {
      if (config.required && !formData[fieldName]?.trim()) {
        return false;
      }
    }

    // Check date validation if both dates exist
    if (formData.issueDate && formData.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.issueDate)) {
        return false;
      }
    }

    return true;
  };

  const validateForm = () => {
    return validatePersonalInfo() && validateDocuments();
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showNotification("Please fill in all required fields correctly", "error");
      return;
    }
    setIsSubmitting(true);
    const files = {
      files: uploadedFiles,
      category: selectedCategory,
      type: "kyc",
      formData,
    };
    kycMutation.mutate(files, {
      onSuccess: (res) => {
        if (res.status) {
          setKycStatus(2)
          queryClient.invalidateQueries({ queryKey: ["kyc-details"] });
          if(kycStatus == 0) {
              setReVerify(false)
          }
          setTimeout(() => {
            setIsSubmitting(false);
            showNotification(res.data,"success");
          }, 1000);
        } else {
          console.log("Failed");
          showNotification(res.data.description || res.data.message, "error");
          setIsSubmitting(false);
        }
      },
      onError: (err) => {
        console.error("error", err);
        showNotification(
          "Failed to submit KYC application. Please try again.",
          "error"
        );
        setIsSubmitting(false);
      },
    });
  };

  const handleFileUpload = (type, event) => {
    const file = event.target.files[0];

    if (file.size > 10 * 1024 * 1024) {
      showNotification("File size should not exceed 10MB", "error");
      return;
    }
    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      showNotification(
        "Unsupported file format. Only JPG, PNG allowed.",
        "error"
      );
      return;
    }

    if (file) {
      setUploadedFiles((prev) => ({
        ...prev,
        [type]: file,
      }));
    }
  };

  // Handle tab navigation with validation
  const handleTabNavigation = (targetTab) => {
    const tabs = ["personal", "documents", "review"];
    const currentIndex = tabs.indexOf(activeTab);
    const targetIndex = tabs.indexOf(targetTab);

    // Allow going back without validation
    if (targetIndex < currentIndex) {
      setActiveTab(targetTab);
      return;
    }

    // Validate current tab before moving forward
    if (activeTab === "personal" && !isPersonalInfoComplete()) {
      if (!validatePersonalInfo()) {
        return;
      }
    }

    if (activeTab === "documents" && !isDocumentsComplete()) {
      if (!validateDocuments()) {
        return;
      }
      if (!uploadedFiles.frontDocument) {
        showNotification("Please upload the front document", "warning");
        return;
      }
      if (!uploadedFiles.backDocument) {
        showNotification("Please upload the back document", "warning");
        return;
      }
    }

    setActiveTab(targetTab);
  };

  // Handle next button click
  const handleNext = () => {
    const tabs = ["personal", "documents", "review"];
    const currentIndex = tabs.indexOf(activeTab);

    if (currentIndex < tabs.length - 1) {
      const nextTab = tabs[currentIndex + 1];
      handleTabNavigation(nextTab);
    }
  };

  // Handle previous button click
  const handlePrevious = () => {
    const tabs = ["personal", "documents", "review"];
    const currentIndex = tabs.indexOf(activeTab);

    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      height: "3rem",
      minHeight: "3.5rem",
      borderColor: state.isFocused ? "black" : provided.borderColor,
      boxShadow: state.isFocused ? "0 0 0 1px black" : provided.boxShadow,
      "&:hover": {
        borderColor: state.isFocused ? "black" : provided.borderColor,
      },
    }),

    singleValue: (provided) => ({
      ...provided,
      color: "black", // selected value text
    }),

    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "black"
        : state.isFocused
        ? "#f0f0f0"
        : "white",
      color: state.isSelected ? "white" : "black",
      cursor: "pointer",
    }),
  };

  const SelectField = ({
    label,
    name,
    value,
    onChange,
    options,
    error,
    required,
  }) => (
    <div className="form-group">
      <label className="form-label">
        {label} {required && <span style={{ color: "#dc3545" }}>*</span>}
      </label>
      <Select
        name={name}
        styles={customStyles}
        options={options}
        value={options.find((opt) => opt.value === value)}
        onChange={onChange}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        isSearchable={false}
      />
      {error && (
        <span className="error-text">
          <AlertCircle size={14} />
          {error}
        </span>
      )}
    </div>
  );

  // Notification Component
  const NotificationComponent = ({ notification, onClose }) => {
    if (!notification) return null;

    const getNotificationClass = (type) => {
      switch (type) {
        case "success":
          return "notification notification-success";
        case "warning":
          return "notification notification-warning";
        case "error":
        default:
          return "notification notification-error";
      }
    };

    const getNotificationIcon = (type) => {
      switch (type) {
        case "success":
          return <CheckCircle size={20} />;
        case "warning":
          return <AlertCircle size={20} />;
        case "error":
        default:
          return <AlertCircle size={20} />;
      }
    };

    return (
      <div className={getNotificationClass(notification.type)}>
        <div className="notification-content">
          {getNotificationIcon(notification.type)}
          <span>{notification.message}</span>
        </div>
        <button onClick={onClose} className="notification-close">
          <X size={16} />
        </button>
      </div>
    );
  };

  if (details.isLoading || isSubmitting) {
    return <Loading />;
  }

  return (
    <div className="kyc-container">
      {/* Notification */}
      <NotificationComponent
        notification={notification}
        onClose={closeNotification}
      />

      <div className="header-container">
        <div style={{ display: "flex", gap: "1rem" }}>
          <Shield size={60} style={{ color: "#e0e0e0" }} />
          <div>
            <h1 className="header-title">KYC Verification</h1>
            <p className="header-subtitle">
              Secure identity verification powered by KYC AID
            </p>
          </div>
        </div>
      </div>

      {!kycDocDetails || reVerify ? (
             <div className="main-content">
             {/* Tab Navigation */}
             <div className="tab-container">
               <div className="tab-navigation">
                 {[
                   { id: "personal", label: "Personal Information", icon: User },
                   { id: "documents", label: "Document Upload", icon: Upload },
                   { id: "review", label: "Review & Submit", icon: CheckCircle },
                 ].map(({ id, label, icon: Icon }) => (
                   <button
                     key={id}
                     onClick={() => handleTabNavigation(id)}
                     className={`tab-button ${activeTab === id ? "active" : ""}`}
                     disabled={
                       (id === "documents" && !isPersonalInfoComplete()) ||
                       (id === "review" &&
                         (!isPersonalInfoComplete() || !isDocumentsComplete()))
                     }
                   >
                     <Icon size={18} />
                     {label}
                   </button>
                 ))}
               </div>
             </div>
   
             {/* Form Content */}
             <div className="form-content">
               {activeTab === "personal" && (
                 <div>
                   <h3 className="section-title">
                     <User size={24} />
                     Personal Information
                   </h3>
                   {!isContactUpdated ? (
                     <ContactNotUpdate />
                   ) : (
                     <div className="form-grid">
                       <InputField
                         label="First Name"
                         name="firstName"
                         value={formData.firstName}
                         onChange={handleInputChange}
                         error={errors.firstName}
                         required
                       />
                       <InputField
                         label="Last Name"
                         name="lastName"
                         value={formData.lastName}
                         onChange={handleInputChange}
                         error={errors.lastName}
                         required
                       />
                       <InputField
                         label="Date of Birth"
                         icon={Calendar}
                         type="date"
                         name="dateOfBirth"
                         value={formData.dateOfBirth}
                         onChange={handleInputChange}
                         error={errors.dateOfBirth}
                         required
                       />
                       <InputField
                         label="Address Line 1"
                         name="address"
                         value={formData.address}
                         onChange={handleInputChange}
                         error={errors.address}
                         required
                       />
   
                       <InputField
                         label="Address Line 2"
                         name="address2"
                         value={formData.address2}
                         onChange={handleInputChange}
                         error={errors.address2}
                         required
                       />
   
                       <InputField
                         label="City"
                         name="city"
                         value={formData.city}
                         onChange={handleInputChange}
                         error={errors.city}
                         required
                       />
   
                       <InputField
                         label="State"
                         name="state"
                         value={formData.state}
                         onChange={handleInputChange}
                         error={errors.state}
                         required
                       />

                      <SelectField
                       label="Country"
                       name="country"
                       value={selectedCountry}
                       onChange={handleCountryChange}
                       options={countryOptions}
                       error={errors.country}
                       required
                     />
   
                       <InputField
                         label="ZIP CODE"
                         name="pin"
                         value={formData.pin}
                         onChange={handleInputChange}
                         error={errors.pin}
                         required
                       />

          {/* ==================== Additional fields not required */}

                       <InputField
                         label="District"
                         name="district"
                         value={formData.district}
                         onChange={handleInputChange}
                         error={errors.district}
                       />

                       <InputField
                         label="Street Name"
                         name="streetName"
                         value={formData.streetName}
                         onChange={handleInputChange}
                         error={errors.streetName}
                       />
         
                       <InputField
                         label="Province"
                         name="province"
                         value={formData.province}
                         onChange={handleInputChange}
                         error={errors.province}
                       />
          

                     </div>
                   )}
                 </div>
               )}
   
               {activeTab === "documents" && (
                 <div>
                   <h3 className="section-title">
                     <FileText size={24} />
                     Document Upload
                   </h3>
   
                   <div className="form-grid">
                     <SelectField
                       label="Document Type"
                       name="documentCategory"
                       value={selectedCategory}
                       onChange={handleCategoryChange}
                       options={documentTypesOptions}
                       error={errors.documentCategory}
                       required
                     />
                     
                     {/* Dynamic document fields based on selected category */}
                     {(() => {
                       const requirements = getCurrentDocumentRequirements();
                       return Object.entries(requirements).map(([fieldName, config]) => {
                         if (fieldName === 'issueDate' || fieldName === 'expiryDate') {
                           return (
                             <InputField
                               key={fieldName}
                               label={config.label}
                               icon={Calendar}
                               type="date"
                               name={fieldName}
                               value={formData[fieldName]}
                               onChange={handleInputChange}
                               error={errors[fieldName]}
                               required={config.required}
                             />
                           );
                         }
                         return (
                           <InputField
                             key={fieldName}
                             label={config.label}
                             name={fieldName}
                             value={formData[fieldName]}
                             onChange={handleInputChange}
                             placeholder={`Enter ${config.label.toLowerCase()}`}
                             error={errors[fieldName]}
                             required={config.required}
                           />
                         );
                       });
                     })()}
                   </div>
   
                   <div style={{ marginTop: "2rem" }}>
                     <div
                       className={`upload-area ${
                         uploadedFiles.frontDocument ? "has-file" : ""
                       }`}
                     >
                       <input
                         type="file"
                         accept="image/*,.pdf"
                         onChange={(e) => handleFileUpload("frontDocument", e)}
                         style={{ display: "none" }}
                         id="front-upload"
                       />
                       <label htmlFor="front-upload" style={{ cursor: "pointer" }}>
                         <Upload size={48} className="upload-icon" />
                         <h4 className="upload-title">Upload Document Front</h4>
                         <p className="upload-description">
                           Click to upload or drag and drop
                           <br />
                           Supported formats: JPG, PNG, PDF (Max 10MB)
                         </p>
                         {uploadedFiles.frontDocument && (
                           <div className="file-info">
                             <CheckCircle size={16} />
                             <span>{uploadedFiles.frontDocument.name}</span>
                           </div>
                         )}
                       </label>
                     </div>
   
                     <div
                       className={`upload-area ${
                         uploadedFiles.backDocument ? "has-file" : ""
                       }`}
                       style={{ marginTop: "1.5rem" }}
                     >
                       <input
                         type="file"
                         accept="image/*,.pdf"
                         onChange={(e) => handleFileUpload("backDocument", e)}
                         style={{ display: "none" }}
                         id="back-upload"
                       />
                       <label htmlFor="back-upload" style={{ cursor: "pointer" }}>
                         <Upload size={48} className="upload-icon" />
                         <h4 className="upload-title">Upload Document Back</h4>
                         <p className="upload-description">
                           Click to upload or drag and drop
                           <br />
                           Supported formats: JPG, PNG, PDF (Max 10MB)
                         </p>
                         {uploadedFiles.backDocument && (
                           <div className="file-info">
                             <CheckCircle size={16} />
                             <span>{uploadedFiles.backDocument.name}</span>
                           </div>
                         )}
                       </label>
                     </div>
                   </div>
                 </div>
               )}
   
               {activeTab === "review" && (
                 <div>
                   <h3 className="section-title">
                     <CheckCircle size={24} />
                     Review & Submit
                   </h3>
   
                   <div className="review-section">
                     <h4 className="review-title">Personal Information</h4>
                     <div className="review-grid">
                       <div className="review-item">
                         <strong>Full Name:</strong>
                         <p>
                           {formData.firstName} {formData.lastName}
                         </p>
                       </div>
                       <div className="review-item">
                         <strong>Date of Birth:</strong>
                         <p>{formData.dateOfBirth}</p>
                       </div>
                       <div className="review-item">
                         <strong>Address Line 1:</strong>
                         <p>{formData.address}</p>
                       </div>
                       <div className="review-item">
                         <strong>Address Line 2:</strong>
                         <p>{formData.address2}</p>
                       </div>
                       <div className="review-item">
                         <strong>City:</strong>
                         <p>{formData.city}</p>
                       </div>
                       <div className="review-item">
                         <strong>State:</strong>
                         <p>{formData.state}</p>
                       </div>
                       <div className="review-item">
                         <strong>ZIP Code:</strong>
                         <p>{formData.pin}</p>
                       </div>
                     </div>
                   </div>
   
                   <div className="review-section">
                     <h4 className="review-title">Document Information</h4>
                     <div className="review-grid">
                       <div className="review-item">
                         <strong>Document Type:</strong>
                         <p>
                           {documentTypesOptions.find(
                             (opt) => opt.value === selectedCategory
                           )?.label || "Not selected"}
                         </p>
                       </div>
                       <div className="review-item">
                         <strong>Document Number:</strong>
                         <p>{formData.documentNumber || "Not provided"}</p>
                       </div>
                       <div className="review-item">
                         <strong>Issue Date:</strong>
                         <p>{formData.issueDate || "Not provided"}</p>
                       </div>
                       <div className="review-item">
                         <strong>Expiry Date:</strong>
                         <p>{formData.expiryDate || "Not provided"}</p>
                       </div>
                       <div className="review-item">
                         <strong>Front Document:</strong>
                         <p>
                           {uploadedFiles.frontDocument
                             ? uploadedFiles.frontDocument.name
                             : "Not uploaded"}
                         </p>
                       </div>
                       <div className="review-item">
                         <strong>Back Document:</strong>
                         <p>
                           {uploadedFiles.backDocument
                             ? uploadedFiles.backDocument.name
                             : "Not uploaded"}
                         </p>
                       </div>
                     </div>
                   </div>
                   <Alert
                     severity="warning"
                     sx={{ fontSize: "0.95rem", alignItems: "center" }}
                   >
                     Please review all information carefully before submitting.
                     Changes may require re-verification after submission.
                   </Alert>
                 </div>
               )}
   
               {/* Action Buttons */}
               <div className="form-actions">
                 {activeTab === "personal" ? (
                   <button
                     type="button"
                     onClick={() => navigate('/profile') }
                     className="btn btn-secondary"
                   >
                     Back
                   </button>
                 ) : (
                   <button
                     type="button"
                     onClick={handlePrevious}
                     disabled={activeTab === "personal"}
                     className="btn btn-secondary"
                   >
                     Previous
                   </button>
                 )}
   
                 {activeTab === "review" ? (
                   <button
                     type="button"
                     onClick={handleSubmit}
                     disabled={isSubmitting}
                     className="btn btn-primary btn-submit"
                   >
                     {isSubmitting ? (
                       <>
                         <span className="loading-spinner"></span>
                         Submitting...
                       </>
                     ) : (
                       <>
                         <Lock size={16} />
                         Submit KYC Application
                       </>
                     )}
                   </button>
                 ) : (
                   <button
                     type="button"
                     onClick={handleNext}
                     className="btn btn-primary"
                   >
                     Next
                   </button>
                 )}
               </div>
             </div>
           </div>
      ) : (
        <KycStatus setReVerify={setReVerify} kycDocData={kycDocDetails} status={kycStatus} />
      )}
    </div>
  );
};

export default KycDetailsCustom;
